// AccessibilitySection.tsx — Settings → Accessibility. Every control is REAL:
//
//   Text size          → same pref the Appearance section edits; consumed by
//                        applyPreferences (scales every fontSize step).
//   Reduce motion      → same pref as Appearance; consumed by RouteTransition,
//                        the Backdrop tier resolver, MediaViewer and SceneCanvas.
//   Underline links    → new pref; consumed by GlobalStyle (theme.underlineLinks).
//   Reduce transparency→ new pref; consumed by applyPreferences, where it forces
//                        cardsGlass off WITHOUT touching the stored Appearance
//                        glass configuration.
//
// Text size and Reduce motion appearing both here and in Appearance is
// deliberate: they are accessibility features first, and a member looking for
// them should find them under either name. Same pref, two doors.
import styled from 'styled-components';
import { Panel, Switch } from '@/ui';
import { usePreferences } from '@/hooks';
import type { TextSizeKey } from '@/core/models';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const RowHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  max-width: 44ch;
`;

const SegmentGroup = styled.div`
  display: flex;
  gap: 2px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
  width: fit-content;
`;

const Segment = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  background: ${({ $active, theme }) => ($active ? theme.color.glassBorder : 'transparent')};
  border: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ $active, theme }) => ($active ? theme.color.textPrimary : theme.color.textMuted)};
  transition: background 0.12s, color 0.12s;
  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: -2px;
  }
`;

/** Text-size segment labels: the standard S / M / L / XL size scale. */
const TEXT_SIZE_LABELS: Record<TextSizeKey, string> = { sm: 'S', md: 'M', lg: 'L', xl: 'XL' };

export function AccessibilitySection() {
  const { prefs, setPreference } = usePreferences();

  return (
    <Stack>
      <Card>
        <CardTitle>Vision</CardTitle>

        <Row>
          <RowText>
            <RowLabel>Text size</RowLabel>
            <RowHint>Scales all text across TruXSocial. Also available under Appearance.</RowHint>
          </RowText>
          <SegmentGroup role="group" aria-label="Text size">
            {(['sm', 'md', 'lg', 'xl'] as TextSizeKey[]).map((key) => (
              <Segment
                key={key}
                type="button"
                $active={prefs.textSize === key}
                aria-pressed={prefs.textSize === key}
                onClick={() => void setPreference('textSize', key)}
              >
                {TEXT_SIZE_LABELS[key]}
              </Segment>
            ))}
          </SegmentGroup>
        </Row>

        <Row>
          <RowText>
            <RowLabel>Underline links</RowLabel>
            <RowHint>Always underline links so they don&apos;t rely on colour alone.</RowHint>
          </RowText>
          <Switch
            checked={prefs.underlineLinks}
            onChange={(v) => void setPreference('underlineLinks', v)}
            label="Underline links"
          />
        </Row>

        <Row>
          <RowText>
            <RowLabel>Reduce transparency</RowLabel>
            <RowHint>
              Make cards fully opaque for maximum contrast. Overrides the Appearance glass
              settings without changing them.
            </RowHint>
          </RowText>
          <Switch
            checked={prefs.reduceTransparency}
            onChange={(v) => void setPreference('reduceTransparency', v)}
            label="Reduce transparency"
          />
        </Row>
      </Card>

      <Card>
        <CardTitle>Motion</CardTitle>

        <Row>
          <RowText>
            <RowLabel>Reduce motion</RowLabel>
            <RowHint>
              Minimise animations and transitions, including the animated background. Also
              available under Appearance.
            </RowHint>
          </RowText>
          <Switch
            checked={prefs.reduceMotion}
            onChange={(v) => void setPreference('reduceMotion', v)}
            label="Reduce motion"
          />
        </Row>
      </Card>
    </Stack>
  );
}
